import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import Layout from './components/Layout';
import Login from './views/Login';
import AuthCallback from './views/AuthCallback';
import Dashboard from './views/Dashboard';
import AdminHome from './views/admin/AdminHome';
import EditAnnouncement from './views/admin/EditAnnouncement';
import ManageAllowlist from './views/admin/ManageAllowlist';
import ManageAlbums from './views/admin/ManageAlbums';
import EditAlbum from './views/admin/EditAlbum';
import UploadPhotos from './views/admin/UploadPhotos';
import Photos from './views/Photos';
import Album from './views/Album';
import { useAuth } from './lib/auth';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function AdminRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/foto" element={<ProtectedRoute><Photos /></ProtectedRoute>} />
          <Route path="/foto/:albumId" element={<ProtectedRoute><Album /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminRoute><AdminHome /></AdminRoute>} />
          <Route path="/admin/announcement" element={<AdminRoute><EditAnnouncement /></AdminRoute>} />
          <Route path="/admin/allowlist" element={<AdminRoute><ManageAllowlist /></AdminRoute>} />
          <Route path="/admin/album" element={<AdminRoute><ManageAlbums /></AdminRoute>} />
          <Route path="/admin/album/:albumId/edit" element={<AdminRoute><EditAlbum /></AdminRoute>} />
          <Route path="/admin/album/:albumId/upload" element={<AdminRoute><UploadPhotos /></AdminRoute>} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
